import {
  ArticleCard,
  Breadcrumbs,
  Error,
  Layout,
  Loader,
  SeoHead,
} from '@components/common'
import AuxSidebar from '@components/common/AuxSidebar'
import MobileAuxSideBar from '@components/common/MobileAuxSidebar'
import { Action, Plus } from '@components/icons'
import { Container, Sidebar } from '@components/ui'
import getGlobalStaticProps from '@lib/globalStaticProps'
import {
  ARTICLE_LISTING_PAGE_NAVIGATION_HANDLE,
  GENERAL_PAGE_SIDE_NAVIGATION_HANDLE,
} from '@lib/helpers/general'
import cn from 'clsx'
import { AnimatePresence } from 'framer-motion'
import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
  OrderByPublishDate,
  createClient,
  getAllDocByType,
  getDocByUid,
} from 'prismicio'
import { useEffect, useRef, useState } from 'react'

export async function getStaticProps({
  params,
  locale,
  previewData,
}: GetStaticPropsContext<{ handle: string[] }>) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)

  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
  ])

  let page = null
  try {
    page = await getDocByUid(
      'article-listing-page',
      params?.handle?.[0]!,
      client,
      locale
    )
  } catch (err) {
    console.log(err)
  }

  const allArticles = await getAllDocByType('article', client, locale)
  const articleCategories = [
    ...new Set(allArticles?.map((article: any) => article?.data?.blogCategory)),
  ]
  const sideNav = await getDocByUid(
    GENERAL_PAGE_SIDE_NAVIGATION_HANDLE,
    ARTICLE_LISTING_PAGE_NAVIGATION_HANDLE,
    client,
    locale
  )

  if (!page) {
    return { notFound: true }
  }

  return {
    props: {
      ...globalProps,
      ...translations,
      page,
      allArticles: allArticles
        ? OrderByPublishDate(allArticles, params?.handle?.[0]!)
        : null,
      articleCategories,
      sideNav: sideNav?.data || null,
    },
    // revalidate: 60,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  // const {
  //   config: { defaultLocale },
  // } = commerceConfig

  // // there are no seperate landing page for other locales in prismic
  // // so we are fetching it only from default locale en-au
  // const pages = await queryAll(
  //   {
  //     query: GET_ALL_ARTICLE_LISTING_PAGES_FOR_FEED,
  //     variables: { lang: defaultLocale },
  //   },
  //   'allArticleListingPages'
  // )

  // const paths = pages
  //   .map((page) => {
  //     const uid = page?.node?._meta?.uid
  //     const pages = uid.split('.')?.filter((s?: string) => s && s !== '')
  //     return locales?.map((locale) => {
  //       return { params: { handle: pages }, locale }
  //     })
  //   })
  //   .flat()

  return {
    paths: [],
    fallback: true,
  }
}

export default function Articles({
  page,
  articleCategories,
  allArticles,
  sideNav,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation('common')
  const router = useRouter()
  const articles: any[] = allArticles || []
  const normalizedCategories: any[] = (articleCategories || []).map(
    (category: string) => t(category?.toLowerCase())
  )
  const [isMenuTriggersticky, setIsMenuTriggerSticky] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const menuTriggerRef = useRef() as React.MutableRefObject<HTMLDivElement>
  const { data } = page || {}
  const [isHover, setIsHover] = useState(false)
  const handleHover = () => {
    setIsHover(true)
  }
  const handleLeave = () => {
    setIsHover(false)
  }
  const {
    title,
    description,
    displayNavTextBlock,
    navLinkText,
    navLinkUrl,
    navTextContent,
    seoTitle,
    seoDescription,
    seoImage,
  } = data || {}
  const { Links }: any = sideNav || []

  let firstCrum = { label: t('the-alchemist'), link: '/the-alchemist' }
  const crumbs = [firstCrum, { label: title, link: router.asPath }]
  const navLinks = Links?.filter((link: any) =>
    normalizedCategories?.includes(link.linkTitle)
  )
  const matchingLink = navLinks?.find(
    (navLink: any) => navLink.link === router.asPath
  )

  const selectedArticleTitle = matchingLink ? matchingLink.linkTitle : null
  const handleScroll = () => {
    if (menuTriggerRef && menuTriggerRef?.current) {
      setIsMenuTriggerSticky(
        menuTriggerRef.current.getBoundingClientRect().top <= 60
      )
    }
  }
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', () => handleScroll)
    }
  }, [])

  const handleClosMobileNav = () => {
    setShowMobileNav(false)
  }
  const handleShowMobileNav = () => {
    setShowMobileNav(true)
  }

  useEffect(() => {
    const handleRouteChange = () => {
      setShowMobileNav(false)
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  if (router.isFallback) {
    return <Loader title="general-page-loader" />
  }

  if (!data) {
    return <Error code={404} />
  }

  return (
    <>
      <SeoHead
        title={seoTitle || title}
        description={seoDescription || description}
        image={seoImage}
      />
      <div className="bg-brand-light-grey">
        <Container className="py-2 md:pt-6 md:pb-4">
          <Breadcrumbs crumbs={crumbs} />
        </Container>
        <div className="wrapper pt-14 pb-24 lg:pt-14">
          <div className="w-full pb-4 text-brand-dark-grey lg:w-160 lg:pb-20">
            {title && (
              <h1 className="pb-4 text-mobileh1 lg:text-h1">{title}</h1>
            )}
            {description && (
              <p className="text-mobileh4 lg:text-h4">{description}</p>
            )}
          </div>
          <div className="w-full">
            <div className="flex-column block gap-x-8 gap-y-24 lg:flex  lg:flex-row">
              <div className="hidden w-full lg:flex lg:w-76 lg:flex-col lg:gap-6">
                <AuxSidebar
                  sideNavigation={ARTICLE_LISTING_PAGE_NAVIGATION_HANDLE}
                  auxItems={navLinks}
                />
                {displayNavTextBlock && (
                  <div>
                    <div className="border-grey block w-full border-t lg:w-76"></div>
                    <div className="body-1 flex flex-col gap-2 py-6 px-4 text-dark-grey">
                      {navTextContent && <div>{navTextContent}</div>}
                      {navLinkUrl && navLinkText && (
                        <div
                          onMouseOver={() => handleHover()}
                          onMouseLeave={() => handleLeave()}
                        >
                          <Link href={navLinkUrl} passHref legacyBehavior>
                            <div className="flex gap-2">
                              <a className="cursor-pointer hover:opacity-75">
                                {navLinkText}
                              </a>
                              <div>
                                {isHover ? (
                                  <Action hover></Action>
                                ) : (
                                  <Action></Action>
                                )}
                              </div>
                            </div>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="sticky top-14 z-30 mb-10 block bg-brand-light-grey pt-2 lg:hidden">
                <div
                  className={cn('transition-all duration-150', {
                    'fixed-component-shadow': isMenuTriggersticky,
                  })}
                  ref={menuTriggerRef}
                >
                  <button
                    onClick={handleShowMobileNav}
                    className=" flex w-full justify-between bg-white py-4 pl-4  pr-4 focus:outline-none lg:hidden"
                  >
                    <div className="body-1-bold text-brand-dark-grey">
                      {selectedArticleTitle}
                    </div>
                    <Plus />
                  </button>
                </div>
              </div>
              <div className="block lg:hidden">
                <AnimatePresence>
                  {Links && Links?.length > 0 && showMobileNav && (
                    <Sidebar
                      onClose={handleClosMobileNav}
                      slideInFromClassName="slideInFromBottom"
                      classNames="w-full"
                    >
                      <MobileAuxSideBar
                        menuItems={navLinks}
                        onClose={handleClosMobileNav}
                        title={'Category'}
                      />
                    </Sidebar>
                  )}
                </AnimatePresence>
              </div>
              {articles && (
                <div className="grid w-full grid-cols-4 gap-6 lg:w-4/5 lg:grid-cols-9 lg:gap-8">
                  {articles?.map((article: any, index: number) => {
                    return (
                      <div
                        className="col-span-4 lg:col-span-3"
                        key={article?.uid || index}
                      >
                        <ArticleCard
                          uid={article?.uid}
                          article={article?.data}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Articles.Layout = Layout
